import type { Department } from "./types";

export const STORAGE_KEY = "lessonIcalDepartments:v2";

export const defaultDepartments: Department[] = [
  { id: "web", name: "Web", codes: ["WD", "W"], color: "#7c3aed", builtin: true },
  { id: "game-art", name: "Game Art", codes: ["GD", "G"], color: "#dc2626", builtin: true },
  { id: "graphic-design-diploma", name: "Graphic Design Diploma", codes: ["ND", "D"], color: "#f472b6", builtin: true },
  { id: "graphic-design-short-course", name: "Graphic Design Short Course", codes: ["GRA"], color: "#ec4899", parentId: "graphic-design-diploma", builtin: true },
  { id: "vfx", name: "VFX", codes: ["FX"], color: "#06b6d4", builtin: true },
  { id: "film", name: "Film", codes: ["FD", "F"], color: "#eab308", builtin: true },
  { id: "video-production-short-course", name: "Video Production Short Course", codes: ["VPC"], color: "#f59e0b", parentId: "film", builtin: true },
  { id: "medienvorkurs", name: "Medienvorkurs", codes: ["MVK"], color: "#f97316", builtin: true },
  { id: "content-creation", name: "Content Creation", codes: ["CD"], color: "#38bdf8", builtin: true },
  { id: "photography-short-course", name: "Photography Short Course", codes: ["PHT", "PH"], color: "#0ea5e9", parentId: "content-creation", builtin: true },
  { id: "3d-production-short-course", name: "3D Production Short Course", codes: ["3DPC"], color: "#ef4444", parentId: "game-art", builtin: true }
];

export const colorOptions = [
  "#7c3aed",
  "#dc2626",
  "#ec4899",
  "#f472b6",
  "#06b6d4",
  "#eab308",
  "#f97316",
  "#38bdf8",
  "#ef4444",
  "#14b8a6",
  "#64748b",
  "#84cc16"
];

export function loadDepartments(): Department[] {
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (!saved) return defaultDepartments;

  try {
    const parsed = JSON.parse(saved) as Department[];
    const custom = parsed.filter((department) => !defaultDepartments.some((item) => item.id === department.id));
    const editedDefaults = defaultDepartments.map((department) => {
      const savedDepartment = parsed.find((item) => item.id === department.id);
      return savedDepartment ? { ...department, ...savedDepartment, parentId: savedDepartment.parentId ?? department.parentId, builtin: true } : department;
    });
    return [...editedDefaults, ...custom];
  } catch {
    return defaultDepartments;
  }
}

export function persistDepartments(departments: Department[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(departments));
}

export function normalizeCode(value: string) {
  return value.trim().toUpperCase();
}

export function splitCodes(value: string) {
  return value
    .split(",")
    .map((code) => normalizeCode(code))
    .filter(Boolean);
}

export function slugify(value: string) {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || `department-${Date.now()}`;
}

export function findDepartmentForCode(rawCode: string, departments: Department[]) {
  const normalized = normalizeCode(rawCode);
  if (!normalized) return null;

  const candidates = departments
    .flatMap((department) =>
      department.codes.map((code) => ({
        code: normalizeCode(code),
        department
      }))
    )
    .filter((item) => item.code)
    .sort((a, b) => b.code.length - a.code.length);

  return candidates.find((item) => normalized === item.code || normalized.startsWith(item.code))?.department ?? null;
}

export function tintColor(hex: string, alpha = 0.15) {
  const clean = hex.replace("#", "");
  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
